import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Clock, Star } from 'lucide-react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import useAuth from '../../hooks/global/useAuth';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useBasket from '../../hooks/basket/useBasket';
import moment from 'moment';
import { useGetCashbackUpcomingExpirationQuery } from '../../store/api-slices';
import { selectCompanyId } from '../../store/slices/InitialDataSlice';

interface Props {
  className?: string;
}

export default function CashbackProgressSection({ className }: Props) {
  const { isLoggedIn } = useAuth();
  const { hasCashbackProgram } = useCashbackProgram();

  if (!hasCashbackProgram) return null;

  return <div className={classnames('w-full', className)}>{isLoggedIn ? <MemberView /> : <GuestView />}</div>;
}

// ── Dual Progress Bar ──
const DualProgressBar = ({
  currentPct,
  earnedPct,
  height = 'h-2',
}: {
  currentPct: number;
  earnedPct: number;
  height?: string;
}) => (
  <div className={classnames('relative w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700', height)}>
    <div
      className="absolute inset-y-0 left-0 bg-brand-primary rounded-full transition-all"
      style={{ width: `${currentPct}%` }}
    />
    <div
      className="absolute inset-y-0 bg-brand-primary/30 rounded-r-full transition-all"
      style={{ left: `${currentPct}%`, width: `${earnedPct}%` }}
    />
  </div>
);

// ── Progress Legend ──
const ProgressLegend = ({ t, earnedPoints }: { t: (key: string, opts?: object) => string; earnedPoints: number }) => (
  <div className="flex items-center gap-md mt-xs">
    <span className="flex items-center gap-2xs text-[10px] text-muted-foreground">
      <span className="inline-block w-2 h-2 rounded-full bg-brand-primary" /> {t('member.current')}
    </span>
    <span className="flex items-center gap-2xs text-[10px] text-muted-foreground">
      <span className="inline-block w-2 h-2 rounded-full bg-brand-primary/30" />{' '}
      {t('member.from_this_order', { points: earnedPoints })}
    </span>
  </div>
);

// ── Guest View (Collapsible) ──
const GuestView = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Cashback:CashbackProgressSection' });
  const { cashbackProgram, calculateExpectedCashback, rewardProducts, pointsToMoney } = useCashbackProgram();
  const { toggleLoginModal, formatCurrency } = useAppMethods();
  const { total } = useBasket();
  const [expanded, setExpanded] = useState(false);

  const earnedPoints = calculateExpectedCashback(total);
  const firstReward = cashbackProgram?.min_spending_limit ?? 50;
  const maxPerOrder = cashbackProgram?.max_spending_limit ?? 0;
  const progress = firstReward > 0 ? Math.min((earnedPoints / firstReward) * 100, 100) : 0;

  const openLogin = () => toggleLoginModal({ openModal: true, onCloseRedirect: () => null });

  return (
    <div className={guestCard}>
      {/* Collapsible header */}
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-xs">
          <Star className="w-5 h-5 fill-brand-primary text-brand-primary shrink-0" />
          <div className="text-left">
            <span className="text-sm font-bold text-brand-text-primary block">
              {t('guest.earn_pts', { points: earnedPoints })}
            </span>
            <span className="text-[11px] text-muted-foreground">{t('guest.tagline')}</span>
          </div>
        </div>
        <div className="flex items-center gap-xs shrink-0">
          <span className="text-xs font-semibold text-brand-primary">
            {earnedPoints}/{firstReward}
          </span>
          <ChevronDown
            className={classnames('w-4 h-4 text-muted-foreground transition-transform duration-200', {
              'rotate-180': expanded,
            })}
          />
        </div>
      </button>

      {/* Collapsed mini progress bar */}
      {!expanded && (
        <div className="mt-xs">
          <DualProgressBar currentPct={0} earnedPct={progress} height="h-1.5" />
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="mt-sm space-y-sm">
          <DualProgressBar currentPct={0} earnedPct={progress} height="h-2.5" />

          {/* Reward examples */}
          {rewardProducts.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-xs">{t('guest.pick_reward')}</p>
              <div className="grid grid-cols-3 gap-xs">
                {rewardProducts.map((product, index) => {
                  const maxOff = pointsToMoney(maxPerOrder);
                  const isFree = product.base_price <= maxOff;
                  const isNearest = index === 0;

                  return (
                    <div
                      key={product.id}
                      className={classnames('relative border rounded-lg p-2.5 text-center', {
                        'border-brand-primary': isNearest,
                        'border-brand-border': !isNearest,
                      })}
                    >
                      {isNearest && <span className={guestNearestBadge}>{t('guest.nearest')}</span>}
                      <p className="text-xs font-semibold text-brand-text-primary mt-1 line-clamp-2">{product.name}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {isFree
                          ? t('guest.free_at', { points: firstReward })
                          : t('guest.save_at', { amount: formatCurrency(maxOff), points: firstReward })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          <button onClick={openLogin} className={guestCtaButton}>
            {t('guest.cta')}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Member View (3 states) ──
const MemberView = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Cashback:CashbackProgressSection' });
  const { cashbackProgram, balance, formatPointsAsMoney, calculateExpectedCashback, earnRate } = useCashbackProgram();
  const { formatCurrency } = useAppMethods();
  const { total } = useBasket();
  const companyId = useSelector(selectCompanyId);
  const { data: upcomingExpirationData } = useGetCashbackUpcomingExpirationQuery(companyId, { skip: !companyId });

  const earnedPoints = calculateExpectedCashback(total);
  const minSpendingLimit = cashbackProgram?.min_spending_limit ?? 0;
  const maxSpendingLimit = cashbackProgram?.max_spending_limit ?? 0;
  const canRedeem = balance >= minSpendingLimit && minSpendingLimit > 0;
  const remaining = Math.max(minSpendingLimit - balance, 0);
  const pointsAfterOrder = balance + earnedPoints;
  const willUnlockAfter = !canRedeem && pointsAfterOrder >= minSpendingLimit;

  const currentPct = minSpendingLimit > 0 ? Math.min((balance / minSpendingLimit) * 100, 100) : 100;
  const earnedPct = minSpendingLimit > 0 ? Math.min((earnedPoints / minSpendingLimit) * 100, 100 - currentPct) : 0;

  // Backend already returns the soonest non-expired bucket; only show it if it falls within the 30-day window
  const expiration = useMemo(() => {
    const upcoming = upcomingExpirationData?.data;

    if (!upcoming?.expires_at || !upcoming?.points) {
      return null;
    }

    const daysUntil = moment(upcoming.expires_at).diff(moment(), 'days');

    if (daysUntil > 30 || daysUntil < 0) {
      return null;
    }

    return { value: upcoming.points, days: daysUntil };
  }, [upcomingExpirationData]);

  // State 1: Early accumulation (far from threshold)
  if (balance < minSpendingLimit * 0.6) {
    return (
      <div className={memberCard}>
        <div className="flex items-center gap-xs mb-2xs">
          <Star className="w-5 h-5 fill-brand-primary text-brand-primary" />
          <span className="text-sm font-bold text-brand-text-primary">{t('member.earning_free')}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-sm">
          {t('member.every_euro', { count: earnRate.count, amount: formatCurrency(earnRate.amountPerUnit) })}
        </p>

        <div className="mb-xs">
          <div className="flex items-center justify-between mb-2xs">
            <span className="text-[11px] text-brand-text-primary font-medium">
              {t('member.until_redeem', { remaining })}
            </span>
            <span className="text-[11px] font-semibold text-brand-primary">
              {balance}/{minSpendingLimit}
            </span>
          </div>
          <DualProgressBar currentPct={currentPct} earnedPct={earnedPct} />
          <ProgressLegend t={t} earnedPoints={earnedPoints} />
        </div>

        {willUnlockAfter && <p className="text-[11px] text-green-600 font-medium mt-xs">{t('member.unlock_first')}</p>}
      </div>
    );
  }

  // State 2: Getting close (close but < threshold)
  if (!canRedeem) {
    const maxRedeemableValue = formatPointsAsMoney(Math.min(pointsAfterOrder, maxSpendingLimit));

    return (
      <div className={memberCard}>
        <div className="flex items-center gap-xs mb-2xs">
          <Star className="w-5 h-5 fill-brand-primary text-brand-primary" />
          <span className="text-sm font-bold text-brand-text-primary">{t('member.so_close')}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-sm">{t('member.just_more', { remaining })}</p>

        <div className="mb-xs">
          <div className="flex items-center justify-between mb-2xs">
            <span className="text-[11px] font-medium text-brand-text-primary">
              {t('member.pts_to_unlock', { remaining })}
            </span>
            <span className="text-[11px] font-semibold text-brand-primary">
              {balance}/{minSpendingLimit}
            </span>
          </div>
          <DualProgressBar currentPct={currentPct} earnedPct={earnedPct} height="h-2.5" />
          <ProgressLegend t={t} earnedPoints={earnedPoints} />
        </div>

        {willUnlockAfter ? (
          <div className={successBox}>
            <p className="text-xs font-semibold text-green-600">
              {t('member.this_order_unlocks', { points: pointsAfterOrder })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {t('member.save_next', { amount: maxRedeemableValue })}
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground mt-xs">
            {t('member.pts_from_order', { earned: earnedPoints, remaining: remaining - earnedPoints })}
          </p>
        )}
      </div>
    );
  }

  // State 3: Can redeem (balance >= threshold)
  const maxRedeemable = Math.min(balance, maxSpendingLimit);
  const maxEuroValue = formatPointsAsMoney(maxRedeemable);
  const euroValue = formatPointsAsMoney(balance);

  return (
    <div className={memberCard}>
      {/* Balance header */}
      <div className="mb-sm">
        <div className="flex items-center justify-between gap-xs">
          <div className="flex items-center gap-xs min-w-0">
            <Star className="w-5 h-5 fill-brand-primary text-brand-primary shrink-0" />
            <span className="text-base font-bold text-brand-text-primary">{balance} pts</span>
            <span className="hidden md:inline text-xs text-muted-foreground">
              {t('member.rewards_value', { amount: euroValue })}
            </span>
          </div>
          {expiration && (
            <span className={expirationBadge}>
              <Clock className="w-3 h-3 inline mr-2xs" />
              {t('member.expiration', { points: expiration.value, count: expiration.days })}
            </span>
          )}
        </div>
        <span className="md:hidden block text-xs text-muted-foreground mt-2xs">
          {t('member.rewards_value', { amount: euroValue })}
        </span>
      </div>

      {/* Redeemable info */}
      <div className={successBox}>
        <p className="text-sm font-semibold text-brand-text-primary">
          {t('member.save_this_order', { amount: maxEuroValue })}
        </p>
        <p className="text-[11px] text-muted-foreground">{t('member.use_pts', { points: maxRedeemable })}</p>
      </div>

      {/* Earnings footer */}
      {earnedPoints > 0 && (
        <div className="flex justify-between text-xs border-t border-brand-primary/10 pt-sm mt-sm">
          <span>
            <span className="text-green-600 font-medium">
              {t('member.earned_this_order', { points: earnedPoints })}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

// Styles
const guestCard = 'rounded-xl border border-brand-border bg-brand-card p-md shadow-sm';
const guestNearestBadge =
  'absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-brand-primary text-brand-button-primary-text rounded-full px-sm py-0.5 whitespace-nowrap';
const guestCtaButton =
  'w-full bg-brand-button-primary-background text-brand-button-primary-text text-sm font-semibold py-sm rounded-xl hover:opacity-90 transition-opacity';
const memberCard = 'rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-md';
const successBox = 'border border-green-500/20 rounded-lg px-sm py-xs bg-green-500/5';
const expirationBadge =
  'text-[11px] font-medium text-red-500 border border-red-500/20 rounded-full px-sm py-2xs whitespace-nowrap';
